/**
 * Authoring registry for the `wf:custom-property` action (STYL-2356).
 *
 * IX3's runtime spreads a property key straight into GSAP's `TweenVars`
 * (AnimationCoordinator `buildTweensForAction`), so *any* name already reaches
 * GSAP. What GSAP does with it varies enormously, and it never reports a
 * failure — an untweenable value simply snaps, and a structurally mismatched
 * one interpolates to visible nonsense. This module is the policy layer that
 * decides which names the Designer offers, and which authored values earn a
 * warning.
 *
 * The tiers come from CSSPlugin's actual decision tree, not from MDN's
 * "animatable" column (which describes what the *browser* would do with a CSS
 * transition — a different question):
 *
 *   Tier A  numeric fast path or color parser. No constraint on the value.
 *   Tier B  `_tweenComplexCSSString`: numbers are extracted from both endpoints
 *           and paired POSITIONALLY, with any missing start slot defaulting to
 *           0 — never to the property's semantic identity. Authorable, but the
 *           panel warns when the endpoints don't line up.
 *   Tier C  no numeric tokens at all, so GSAP applies the end value discretely
 *           at the first render past progress 0. Not offered.
 *
 * Kept free of `CSS_PROPERTY_DETAILS` on purpose: that constant is a ~5.4k-line
 * generated file, and this module is reachable from the schema package.
 *
 * What this module deliberately does NOT touch — CSSPlugin already owns it, and
 * second-guessing it would be both redundant and a source of drift:
 *
 *   - `margin`/`padding`/`borderWidth`/`borderRadius` shorthands, fanned out to
 *     their longhands by `_specialProps` rather than string-interpolated.
 *   - `height: "auto"`, measured by temporarily applying the value.
 *   - Transform aliases and `transformOrigin`, which route through GSAP's
 *     decomposed transform cache (and are denied here for that reason).
 *   - Color normalization. `_colorStringFilter` already reorders the start's
 *     colors to match the end's, which is what makes `boxShadow` and
 *     `drop-shadow()` interpolate even when the browser reports the color
 *     first and the author wrote it last.
 *   - Unit conversion for ordinary dimensional properties, where the number is
 *     the whole value and GSAP's end-of-string unit fallback always fires.
 */

import {
  STYLE_ACTION_PROPERTIES,
  TRANSFORM_ACTION_PROPERTIES,
} from '../actionPropertyAllowlist';
import {describeBasicShapeIssue, normalizeClipPathPair} from './basicShapes';

/**
 * Tier A — length/numeric properties GSAP takes down its numeric fast path.
 *
 * Requires `parseFloat` to succeed on both ends, no space/comma/paren followed
 * by non-space in the end value, and the name to exist in `style` (GSAP falls
 * back to vendor-prefix probing). Units are reconciled automatically, including
 * px/%/em/rem/vw/vh conversion.
 */
const TIER_A_NUMERIC: readonly string[] = [
  // Intrinsic sizing (width/height themselves are owned by wf:transform)
  'maxWidth',
  'maxHeight',
  // `aspectRatio` is left out on purpose: `16 / 9` is a ratio, and the
  // computed start is `auto`, which has no number. GSAP starts the tween from
  // 0, so the box is far too tall for the first frames.
  // Spacing. The shorthands are safe because GSAP fans them out to four
  // sides via `_specialProps` rather than string-interpolating them.
  'margin',
  'padding',
  // Typography
  'fontSize',
  'fontWeight',
  'letterSpacing',
  'lineHeight',
  // Borders and outlines — also `_specialProps` fan-outs
  'borderRadius',
  'borderWidth',
  // Flex and grid
  'gap',
  'rowGap',
  'columnGap',
  // SVG geometry that is also a CSS property
  'strokeWidth',
  'strokeDashoffset',
  // Single-axis background offsets. The `backgroundPosition` SHORTHAND is
  // Tier B (keywords, multi-layer lists); the per-axis longhands are plain
  // numbers.
  'backgroundPositionX',
  'backgroundPositionY',
];

/**
 * Tier A — colors, routed through GSAP's `splitColor` and interpolated per
 * channel. `color`, `backgroundColor` and `borderColor` are absent because
 * wf:style already owns them (see ACTION_OWNED_PROPERTIES).
 *
 * Only the notations `splitColor` recognizes are safe — see
 * UNSUPPORTED_COLOR_NOTATIONS.
 */
const TIER_A_COLOR: readonly string[] = [
  'textDecorationColor',
  'fill',
  'stroke',
];

/**
 * Tier B — complex strings. Tweenable, but only when both endpoints carry the
 * same numeric tokens in the same order. The panel warns rather than blocks.
 */
const TIER_B: readonly string[] = [
  'filter',
  'backdropFilter',
  'clipPath',
  'boxShadow',
  'textShadow',
  'backgroundPosition',
  'backgroundSize',
  'maskPosition',
  'maskSize',
  'objectPosition',
  'perspectiveOrigin',
  'strokeDasharray',
];

/**
 * CSS transform properties that must never be authored here.
 *
 * GSAP keeps a decomposed transform cache on `target._gsap` and composes one
 * matrix per tick. A raw `transform` string routes through
 * `_addRawTransformPTs`, which parses back into that same cache — so a custom
 * property writing `transform` and a wf:transform action writing `x`/`rotation`
 * on the same element desync. Raw strings also lose rotational precision: the
 * matrix for 360deg is identical to the one for 0deg and 720deg.
 */
const DENIED_TRANSFORM_SHORTHANDS: readonly string[] = [
  'transform',
  'translate',
  'rotate',
  'scale',
];

/**
 * Properties already offered by wf:transform and wf:style. Hidden from this
 * menu so one property has exactly one authoring path — two actions tweening
 * the same key on one target would produce competing GSAP tweens.
 */
export const ACTION_OWNED_PROPERTIES: ReadonlySet<string> = new Set([
  ...TRANSFORM_ACTION_PROPERTIES,
  ...STYLE_ACTION_PROPERTIES,
]);

export const CUSTOM_PROPERTY_TIER_A: ReadonlySet<string> = new Set([
  ...TIER_A_NUMERIC,
  ...TIER_A_COLOR,
]);

export const CUSTOM_PROPERTY_TIER_B: ReadonlySet<string> = new Set(TIER_B);

export const CUSTOM_PROPERTY_DENIED_SHORTHANDS: ReadonlySet<string> = new Set(
  DENIED_TRANSFORM_SHORTHANDS
);

/**
 * Color notations GSAP 3.15.0 does NOT recognize. `splitColor` matches named
 * colors, #RGB/#RGBA/#RRGGBB/#RRGGBBAA, rgb()/rgba() and hsl()/hsla() only.
 *
 * Everything here falls through to positional string interpolation (so an
 * oklch() tween interpolates three raw components with no hue shortest-path and
 * no gamut mapping) or, for `currentColor`, to a discrete snap. Webflow's own
 * style system already accepts oklch/oklab, and Variables can hold
 * color-mix(), so these do reach us in practice.
 */
export const UNSUPPORTED_COLOR_NOTATIONS: readonly string[] = [
  'oklch',
  'oklab',
  'lab',
  'lch',
  'color-mix',
  'currentcolor',
];

/**
 * A CSS custom property: two leading dashes, then one or more name chars and
 * nothing else. The whole name is checked because it becomes a stored key and a
 * `setProperty` argument, and the browser silently ignores an invalid one.
 */
export function isCssVariableName(property: string): boolean {
  return /^--[A-Za-z0-9_-]+$/.test(property);
}

/**
 * Whether lowercased `value` uses `notation` as a token: a function only as a
 * call, so `var(--label-color)` does not count as `lab()`.
 */
function usesColorNotation(value: string, notation: string): boolean {
  for (
    let index = value.indexOf(notation);
    index !== -1;
    index = value.indexOf(notation, index + 1)
  ) {
    const before = index === 0 ? '' : value[index - 1]!;
    const after = value[index + notation.length] ?? '';
    if (/[\w-]/.test(before)) continue;
    if (notation === 'currentcolor' ? !/[\w-]/.test(after) : after === '(') {
      return true;
    }
  }
  return false;
}

export type CustomPropertyClassification =
  | 'tier-a'
  | 'tier-b'
  | 'css-variable'
  | 'denied-owned'
  | 'denied-transform'
  | 'unknown';

/**
 * Classify an author-typed property name. `unknown` covers anything not in the
 * curated tiers — an unrecognized or misspelled name, or a valid CSS property
 * we have not triaged. Treated as not-offerable.
 */
export function classifyCustomProperty(
  property: string
): CustomPropertyClassification {
  if (isCssVariableName(property)) return 'css-variable';
  if (CUSTOM_PROPERTY_DENIED_SHORTHANDS.has(property))
    return 'denied-transform';
  if (ACTION_OWNED_PROPERTIES.has(property)) return 'denied-owned';
  if (CUSTOM_PROPERTY_TIER_A.has(property)) return 'tier-a';
  if (CUSTOM_PROPERTY_TIER_B.has(property)) return 'tier-b';
  return 'unknown';
}

/**
 * Resolve a hyphenated CSS spelling to the camelCase name the tiers use.
 *
 * Authors type `font-size` as readily as `fontSize` — both name the same
 * property, and only one of them is a name this module recognizes. Rejecting
 * the hyphenated spelling reports a problem the author does not have, so
 * canonicalize it instead and keep the rejection for names that resolve to
 * nothing either way.
 *
 * Returns the name unchanged when it is already recognized, when it is a CSS
 * variable (where the dashes are the name), or when camel-casing it lands
 * somewhere just as unknown.
 *
 * The panel no longer calls this while the author types — it stores the
 * spelling they used. This runs in the transform instead, so the published and
 * previewed animation always carries the name GSAP recognizes.
 */
export function canonicalizeCustomPropertyName(property: string): string {
  if (isCssVariableName(property)) return property;
  if (classifyCustomProperty(property) !== 'unknown') return property;

  const camelCased = property
    .toLowerCase()
    .replace(/-([a-z])/g, (_, char: string) => char.toUpperCase());

  return classifyCustomProperty(camelCased) === 'unknown'
    ? property
    : camelCased;
}

/**
 * Whether a name may be persisted under wf:custom-property at all.
 *
 * Canonicalizes first: the panel stores the spelling the author typed, so a
 * perfectly good `font-size` reaches here in a form the tiers do not list.
 */
export function isCustomPropertyAllowed(property: string): boolean {
  const tier = classifyCustomProperty(canonicalizeCustomPropertyName(property));
  return tier === 'tier-a' || tier === 'tier-b' || tier === 'css-variable';
}

/**
 * Explains why a name is not allowed, for the invariant's error message.
 * Returns null when the name is fine.
 *
 * Classifies the canonical name but quotes the one it was given, so an author
 * who typed `background-color` is told about `background-color`.
 */
export function describeCustomPropertyRejection(
  property: string
): string | null {
  switch (classifyCustomProperty(canonicalizeCustomPropertyName(property))) {
    case 'denied-transform':
      return (
        `"${property}" cannot be animated as a custom property because it ` +
        `would conflict with the Transform properties. Use those instead.`
      );
    case 'denied-owned':
      return (
        `"${property}" is already in Animated properties. Add it from there, ` +
        `so two animations do not compete for it.`
      );
    case 'unknown':
      return (
        `"${property}" is not a recognized animatable CSS property. Custom ` +
        `properties must be a supported CSS property or a CSS variable ` +
        `(for example "--my-var").`
      );
    default:
      return null;
  }
}

const TIER_A_COLOR_SET: ReadonlySet<string> = new Set(TIER_A_COLOR);

/**
 * Whether a property's values are colors, so the panel offers a color picker
 * and a random value-set rather than a unit input and a min/max range.
 */
export function isCustomPropertyColor(property: string): boolean {
  return TIER_A_COLOR_SET.has(canonicalizeCustomPropertyName(property));
}

/**
 * The label the Designer shows for each offered property.
 *
 * The Animated properties list above this section reads "Move X" and "Scale",
 * never `xPercent` or `scaleX`, and the two lists are peers on one action — so
 * these are written the same way rather than derived from the camelCase name.
 * Deriving them would also get `backgroundPositionX` wrong, where the trailing
 * axis letter stays capitalized and the rest does not.
 */
export const CUSTOM_PROPERTY_LABELS: Readonly<Record<string, string>> = {
  // Tier A — numeric
  maxWidth: 'Max width',
  maxHeight: 'Max height',
  margin: 'Margin',
  padding: 'Padding',
  fontSize: 'Font size',
  fontWeight: 'Font weight',
  letterSpacing: 'Letter spacing',
  lineHeight: 'Line height',
  borderRadius: 'Border radius',
  borderWidth: 'Border width',
  gap: 'Gap',
  rowGap: 'Row gap',
  columnGap: 'Column gap',
  strokeWidth: 'Stroke width',
  strokeDashoffset: 'Stroke dashoffset',
  backgroundPositionX: 'Background position X',
  backgroundPositionY: 'Background position Y',
  // Tier A — color
  textDecorationColor: 'Text decoration color',
  fill: 'Fill',
  stroke: 'Stroke',
  // Tier B
  filter: 'Filter',
  backdropFilter: 'Backdrop filter',
  clipPath: 'Clip path',
  boxShadow: 'Box shadow',
  textShadow: 'Text shadow',
  backgroundPosition: 'Background position',
  backgroundSize: 'Background size',
  maskPosition: 'Mask position',
  maskSize: 'Mask size',
  objectPosition: 'Object position',
  perspectiveOrigin: 'Perspective origin',
  strokeDasharray: 'Stroke dasharray',
};

/**
 * How a property name should read in the panel.
 *
 * Falls back to the name itself, which is what an author-typed name wants: a
 * `--my-var` or a spelling we do not offer is shown exactly as it was entered
 * rather than rewritten into something the author did not choose.
 */
export function getCustomPropertyLabel(property: string): string {
  // Own properties only: `constructor`, `toString` etc. would otherwise resolve
  // to inherited functions on the object literal.
  return Object.hasOwn(CUSTOM_PROPERTY_LABELS, property)
    ? CUSTOM_PROPERTY_LABELS[property]!
    : property;
}

/**
 * Every property name the Designer offers in the custom-property typeahead,
 * ordered by the label the menu actually shows. CSS variables are not listed —
 * they are author-typed and unbounded, so the panel accepts any `--name` as
 * free text.
 */
export const CUSTOM_PROPERTY_SUGGESTIONS: readonly string[] = [
  ...TIER_A_NUMERIC,
  ...TIER_A_COLOR,
  ...TIER_B,
].sort((a, b) =>
  getCustomPropertyLabel(a).localeCompare(getCustomPropertyLabel(b))
);

/** Classifications whose values a property mode can meaningfully wrap. */
const MODE_CAPABLE_TIERS: ReadonlySet<CustomPropertyClassification> = new Set([
  'tier-a',
  'css-variable',
]);

/**
 * Property-mode capability, mirroring how wf:transform and wf:style are
 * configured: numeric properties get min/max Random and Additive, colors get
 * the Random value-set only.
 *
 * Tier B is excluded on purpose — `+=` or `random(a, b)` against a complex
 * string like `blur(4px) brightness(1.2)` has no coherent meaning.
 *
 * CSS variables are included. GSAP does infer a variable's unit from the
 * property NAME (`_convertToUnit` matches /(left|right|width|margin|padding|x)/i),
 * which `--my-var` tells it nothing about — but that inference is only reached
 * for a value that arrives without one, and these wrappers carry their own
 * `unit` straight into the emitted GSAP string (`applyAdditive` writes
 * `+=8px`, `makeRandomPicker` writes `10px`). The panel is what has to hold up
 * its end: it seeds the wrapper's unit from the authored text, so the name is
 * never the thing being asked.
 */
export function customPropertySupportsMode(
  mode: 'random-array' | 'random-range' | 'additive',
  property: string
): boolean {
  // Canonicalized for the same reason as `describeCustomPropertyRejection`:
  // callers hold the author's spelling, not the tier table's.
  const canonical = canonicalizeCustomPropertyName(property);
  if (!MODE_CAPABLE_TIERS.has(classifyCustomProperty(canonical))) return false;
  const isColor = TIER_A_COLOR_SET.has(canonical);
  return mode === 'random-array' ? true : !isColor;
}

// ---------------------------------------------------------------------------
// Value-shape analysis (Tier B warnings)
// ---------------------------------------------------------------------------

/**
 * GSAP's own numeric-token pattern (`_complexStringNumExp` in gsap-core).
 * Rebuilt per call — a shared /g regex carries `lastIndex` between calls.
 */
const NUM_WITH_UNIT_SOURCE = /[-+=.]*\d+[.e-]*\d*[a-z%]*/.source;

/** Numeric tokens GSAP would pair positionally across the two endpoints. */
export function numericTokens(value: string): string[] {
  return value.match(new RegExp(NUM_WITH_UNIT_SOURCE, 'g')) ?? [];
}

/** The ordered function names in a value, e.g. ['brightness', 'blur']. */
export function functionSequence(value: string): string[] {
  const found = value.match(/[A-Za-z][A-Za-z0-9-]*(?=\s*\()/g) ?? [];
  return found.map((name) => name.toLowerCase());
}

/**
 * Functions whose arguments are unitless by definition. Attaching a unit inside
 * one would make the value invalid rather than fix it.
 */
const UNITLESS_LEGAL_FUNCTIONS: ReadonlySet<string> = new Set([
  'brightness',
  'contrast',
  'saturate',
  'grayscale',
  'sepia',
  'invert',
  'opacity',
]);

/** Color functions carry bare channel numbers; GSAP tracks these separately. */
const COLOR_FUNCTIONS: ReadonlySet<string> = new Set([
  'rgb',
  'rgba',
  'hsl',
  'hsla',
]);

/** Properties whose values are legitimately unitless throughout. */
const UNITLESS_LEGAL_PROPERTIES: ReadonlySet<string> = new Set([
  'strokeDasharray',
  'aspectRatio',
  'lineHeight',
  'flexGrow',
  'flexShrink',
  'zIndex',
  'order',
  'opacity',
  'columnCount',
  'fontWeight',
]);

type NumericSlot = {
  /** The matched token, e.g. `10px` or `0`. */
  token: string;
  /** Offset of the token in the source string. */
  start: number;
  /** Innermost enclosing function name, lowercased; empty at top level. */
  fn: string;
  /** True anywhere inside a color function. */
  inColor: boolean;
};

/**
 * The numeric tokens GSAP would pair, each tagged with the function it sits
 * inside. `numericTokens` alone is not enough here: whether a bare number is a
 * defect depends entirely on its context — `brightness(0)` is valid CSS and
 * `blur(0)` is not.
 */
function scanNumericSlots(value: string): NumericSlot[] {
  const slots: NumericSlot[] = [];
  const stack: string[] = [];
  const numberAt = new RegExp(NUM_WITH_UNIT_SOURCE, 'y');
  let index = 0;

  while (index < value.length) {
    const char = value[index]!;

    if (char === '(') {
      const ident = /([a-z][a-z0-9-]*)$/i.exec(value.slice(0, index));
      stack.push(ident ? ident[1]!.toLowerCase() : '');
      index++;
      continue;
    }
    if (char === ')') {
      stack.pop();
      index++;
      continue;
    }

    numberAt.lastIndex = index;
    const match = numberAt.exec(value);
    if (match) {
      slots.push({
        token: match[0],
        start: index,
        fn: stack[stack.length - 1] ?? '',
        inColor: stack.some((name) => COLOR_FUNCTIONS.has(name)),
      });
      index = numberAt.lastIndex;
      continue;
    }

    index++;
  }

  return slots;
}

function hasUnit(token: string): boolean {
  return /[a-z%]$/i.test(token);
}

/**
 * Indices into the end value's numeric tokens whose interpolated mid-frames
 * would be invalid CSS.
 *
 * `_renderComplexString` rebuilds every intermediate frame from the END
 * string's literal text plus bare numbers, so a unit survives only where it
 * already appears in that string. `filter: blur(10px)` → `blur(0)` therefore
 * renders `blur(5)` at the halfway point, which the browser drops — the element
 * holds its last valid value and then snaps at ratio 1, where GSAP
 * short-circuits back to the raw authored string.
 *
 * Only the end value is scanned. The start's units are irrelevant to whether a
 * frame parses; GSAP reconciles them via `_convertToUnit`.
 */
export function findUnitlessInterpolationSlots(
  property: string,
  from: string | undefined,
  to: string
): number[] {
  if (UNITLESS_LEGAL_PROPERTIES.has(canonicalizeCustomPropertyName(property)))
    return [];

  const trimmedTo = to.trim();
  const toSlots = scanNumericSlots(trimmedTo);
  const fromSlots = from == null ? null : scanNumericSlots(from.trim());

  const unsafe: number[] = [];
  toSlots.forEach((slot, index) => {
    if (hasUnit(slot.token) || slot.inColor) return;
    if (UNITLESS_LEGAL_FUNCTIONS.has(slot.fn)) return;

    // GSAP appends a fallback unit, but only when the number is the last thing
    // in the string (`if (index === end.length)`).
    if (slot.start + slot.token.length === trimmedTo.length) return;

    // A slot whose endpoints are the same STRING never becomes a PropTween
    // (`if (endValue !== startValue)`), so its literal text survives verbatim
    // into every frame. String equality, not numeric — `0` and `0px` differ.
    if (fromSlots?.[index]?.token === slot.token) return;

    unsafe.push(index);
  });

  return unsafe;
}

/**
 * Rewrite a from/to pair so every interpolation slot survives into mid-frames.
 *
 * Deliberately conservative outside clip-path: a unit is attached only when the
 * opposing endpoint already tells us which one to use. Guessing `px` for an
 * untriaged grammar could turn a valid value into an invalid one, which is the
 * exact failure this is meant to prevent. Slots we cannot infer still earn a
 * warning — see `findCustomPropertyWarnings`.
 */
export function normalizeCustomPropertyPair(
  property: string,
  from: string | undefined,
  to: string
): {from: string | undefined; to: string; changed: boolean} {
  // The panel stores the spelling the author typed, so `clip-path` arrives here
  // as often as `clipPath`.
  const canonical = canonicalizeCustomPropertyName(property);

  // clip-path has a closed, fully-specified grammar, so its slot types are
  // known and it can canonicalize far more than the generic rule.
  if (canonical === 'clipPath') {
    const shaped = normalizeClipPathPair(from, to);
    return {from: shaped.from, to: shaped.to, changed: shaped.changed};
  }

  if (classifyCustomProperty(canonical) !== 'tier-b')
    return {from, to, changed: false};

  const trimmedTo = to.trim();
  const unsafe = findUnitlessInterpolationSlots(property, from, trimmedTo);
  if (unsafe.length === 0 || from == null) return {from, to, changed: false};

  const toSlots = scanNumericSlots(trimmedTo);
  const fromSlots = scanNumericSlots(from.trim());

  // Splice from the end so earlier offsets stay valid.
  let next = trimmedTo;
  let changed = false;
  for (const index of [...unsafe].reverse()) {
    const slot = toSlots[index];
    const paired = fromSlots[index];
    if (!slot || !paired) continue;

    const unit = /[a-z%]+$/i.exec(paired.token)?.[0];
    if (!unit) continue;

    next =
      next.slice(0, slot.start) +
      slot.token +
      unit +
      next.slice(slot.start + slot.token.length);
    changed = true;
  }

  return changed ? {from, to: next, changed} : {from, to, changed: false};
}

export type CustomPropertyWarning = {
  code:
    | 'unitless-interpolation-slot'
    | 'shape-not-interpolable'
    | 'token-count-mismatch'
    | 'function-sequence-mismatch'
    | 'implicit-start'
    | 'keyword-value'
    | 'unsupported-color-notation'
    | 'variable-start-unresolved'
    /** Raised by the Designer, which knows the site's variables. */
    | 'webflow-variable-name';
  message: string;
};

/** Values with no numbers for GSAP to grab onto. */
const ZERO_TOKEN_KEYWORDS = new Set([
  'none',
  'auto',
  'normal',
  'initial',
  'inherit',
  'unset',
  'revert',
  'cover',
  'contain',
  'center',
  'top',
  'right',
  'bottom',
  'left',
]);

/**
 * Non-blocking authoring warnings for a single custom-property row.
 *
 * Every one of these describes a real, silent runtime failure — GSAP does not
 * error on any of them. Deliberately advisory: authors keep full freedom, and
 * the panel surfaces the risk on the row.
 *
 * @param property   author-typed name (CSS property or `--var`)
 * @param from       start value; undefined for a bare `to` tween, where GSAP
 *                   reads the start from computed style
 * @param to         end value
 */
export function findCustomPropertyWarnings({
  property,
  from,
  to,
}: {
  property: string;
  from?: string;
  to: string;
}): CustomPropertyWarning[] {
  const warnings: CustomPropertyWarning[] = [];
  // Classify the canonical name, but quote the spelling the author typed.
  const canonical = canonicalizeCustomPropertyName(property);
  const tier = classifyCustomProperty(canonical);
  const trimmedTo = to.trim();
  const trimmedFrom = from?.trim();

  const lowerTo = trimmedTo.toLowerCase();
  const lowerFrom = trimmedFrom?.toLowerCase();
  for (const notation of UNSUPPORTED_COLOR_NOTATIONS) {
    if (
      usesColorNotation(lowerTo, notation) ||
      (lowerFrom != null && usesColorNotation(lowerFrom, notation))
    ) {
      const spelled =
        notation === 'currentcolor' ? 'currentColor' : `${notation}()`;
      warnings.push({
        code: 'unsupported-color-notation',
        message:
          `The animation engine does not read ${spelled} as a color. It ` +
          `animates the raw numbers instead, which can produce unexpected ` +
          `hues. Use hex, rgb(), rgba(), hsl() or hsla().`,
      });
      break;
    }
  }

  // A `--var` with no authored start leans on getComputedStyle, which returns
  // "" when the variable is not defined on the target. parseFloat("") is 0 and
  // carries no unit, so the tween silently starts from zero.
  if (tier === 'css-variable' && trimmedFrom == null) {
    warnings.push({
      code: 'variable-start-unresolved',
      message:
        `"${property}" has no start value. If the variable is not defined on ` +
        `the target element, the animation starts from 0 with no unit. Set an ` +
        `explicit start value to be sure.`,
    });
  }

  // The default line height computes to `normal`, which has no number. With no
  // authored start, GSAP starts the tween from 0, so the text collapses as soon
  // as the tween starts and then grows to the end value.
  if (canonical === 'lineHeight' && trimmedFrom == null) {
    warnings.push({
      code: 'implicit-start',
      message:
        `${property} has no start value, so the animation reads it from the ` +
        `element's current style. If that is the default line height ("normal"), the ` +
        `animation starts from 0 and the text collapses. Set an explicit ` +
        `start value.`,
    });
  }

  if (tier !== 'tier-b') return warnings;

  const toTokens = numericTokens(trimmedTo);

  if (ZERO_TOKEN_KEYWORDS.has(lowerTo)) {
    warnings.push({
      code: 'keyword-value',
      message:
        `"${trimmedTo}" contains no numbers to animate, so ` +
        `${property} will jump to it rather than animate.`,
    });
  }

  // clip-path's own grammar sees structural problems that token counting
  // cannot: CSS lets almost every slot be omitted, and the browser's
  // serializer does not fill the omitted ones back in.
  if (canonical === 'clipPath') {
    const shaped = normalizeClipPathPair(trimmedFrom, trimmedTo);
    if (shaped.issue) {
      warnings.push({
        code: 'shape-not-interpolable',
        message: describeBasicShapeIssue(shaped.issue),
      });
    }
  }

  // Ranked above token-count-mismatch: a mismatched count animates to something
  // wrong, but an unitless slot renders no frames at all.
  if (findUnitlessInterpolationSlots(property, trimmedFrom, trimmedTo).length) {
    warnings.push({
      code: 'unitless-interpolation-slot',
      message:
        `Some values in "${trimmedTo}" have no unit. The animation engine rebuilds every ` +
        `in-between frame from this value and writes the numbers back without ` +
        `units, so the browser rejects each frame and ${property} holds still ` +
        `until it jumps at the end. Add a unit to every number.`,
    });
  }

  if (trimmedFrom == null) {
    warnings.push({
      code: 'implicit-start',
      message:
        `${property} has no start value, so the animation reads it from the ` +
        `element's current style. If that is "none" or a keyword, every missing number ` +
        `defaults to 0 — a filter starting from "none" flashes through black. ` +
        `Set an explicit start value with the same structure as the end value.`,
    });
    return warnings;
  }

  const fromTokens = numericTokens(trimmedFrom);
  if (fromTokens.length !== toTokens.length) {
    warnings.push({
      code: 'token-count-mismatch',
      message:
        `Start and end values have different numbers of values ` +
        `(${fromTokens.length} vs ${toTokens.length}). The animation pairs them in ` +
        `order and fills any gap with 0, so the animation will not look ` +
        `right. Give both values the same structure.`,
    });
  }

  const fromFns = functionSequence(trimmedFrom);
  const toFns = functionSequence(trimmedTo);
  const sameSequence =
    fromFns.length === toFns.length &&
    fromFns.every((name, index) => name === toFns[index]);
  if (!sameSequence) {
    warnings.push({
      code: 'function-sequence-mismatch',
      message:
        `Start and end values use different functions ` +
        `(${fromFns.join(', ') || 'none'} vs ${toFns.join(', ') || 'none'}). ` +
        `The animation matches numbers by position, not by function name, so these ` +
        `will cross-interpolate. Use the same functions in the same order.`,
    });
  }

  return warnings;
}
